import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  CarrierType,
  CarrierVisitDirection,
  ContainerDamageResponseDto,
  OrderResponseDto,
  SealsDto,
  UnitType,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { IContainerDamage } from '@planning/components'
import { ControlledContainerSealsWithTypes } from '@planning/components/ControlledContainerSealsWithTypes'
import { ControlledDamageRecordBox } from '@planning/components/damage-report/ControlledDamageRecordBox'
import { OogFormData } from '@planning/components/organisms/OogSubForm/oog-helper'
import { OogSubForm } from '@planning/components/organisms/OogSubForm/OogSubForm'
import { UnitLabelSubForm } from '@planning/components/organisms/UnitLabelSubForm/UnitLabelSubForm'
import { StowagePositionFormData } from '@planning/components/stowage-position/stores/StowagePositionSubFormStore'
import { IContainerItem } from '@planning/rt-stores/container/ContainerItem'
import { validateContainerNumber } from '@planning/rt-stores/helpers'
import { useTranslate } from '@tolgee/react'
import { CustomInputAdornment, Tile, useMinimalsTheme } from '@tom-ui/ui'
import { notAllowPlusMinusPointSign, onlyAllowPositiveInt, useFormStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { InspectContainerAlert, InspectContainerAlertType } from '../atoms/InspectContainerAlert'

interface IProps {
  damages?: ContainerDamageResponseDto[]
  containerItem?: IContainerItem
  containerAutocomplete?: React.ReactNode
  isOverlanded?: boolean
  inspectContainerAlertType?: InspectContainerAlertType
  containerStatus?: string
  onContainerNumberChange?: (containerNumber: string) => Promise<void> | void
}

export interface IInspectContainerFormData extends OrderResponseDto {
  carrierType: CarrierType
  carrierVisitId: number | null
  damages: ContainerDamageResponseDto[]
  damagesReported?: IContainerDamage[]
  hasDamage?: boolean
  oog?: OogFormData
  stowagePosition?: StowagePositionFormData
  containerNumber?: string | null
  containerIsoCode?: string | null
  isEmpty: boolean
  notes?: string | null
  hasSeals?: boolean
  seals?: SealsDto[] | null
  containerMaxGross?: number | null
  containerTare?: number | null
  consignee?: string | null
}

export const InspectContainerForm: FC<IProps> = observer(
  ({
    containerItem,
    containerAutocomplete,
    isOverlanded,
    containerStatus,
    inspectContainerAlertType,
    onContainerNumberChange,
  }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { isoCodeMappingInputStore } = usePlanningStore()

    const formStore = useFormStore<IInspectContainerFormData>()

    const { control, getValues, setValue, watch, formState } =
      useFormContext<IInspectContainerFormData>()

    const { errors } = formState

    const hasDoorDirection = !!getValues('doorDirection')
    const hasOperationalInstructions = !!getValues('operationalInstructions')
    const direction = getValues('direction')
    const portOfDischarge = getValues('portOfDischarge')
    const portOfLoading = getValues('portOfLoading')
    const vgm = getValues('vgm')
    const gwt = getValues('grossWeight')
    const shippingLine = getValues('shippingLine')
    const journeyCategory = getValues('journeyCategory')

    const displayGeneralInformation =
      !!portOfDischarge || !!vgm || !!gwt || !!shippingLine || !!journeyCategory

    const unitTypeIsContainer = containerItem?.data.unitType === UnitType.Container

    const containerNumber = watch('containerNumber')?.trim().toUpperCase()
    const showInvalidContainerNumberInfoMessage = useMemo(
      () =>
        containerNumber && !validateContainerNumber(containerNumber) && containerNumber.length > 0,
      [containerNumber],
    )

    return (
      <form>
        <Grid container gap={theme.customSpacing.m} pb={theme.customSpacing.l}>
          {displayGeneralInformation && (
            <Grid size={12}>
              <Typography variant='subtitle1'>
                {t('generalInformation', 'General information')}
              </Typography>
              <Stack
                direction='row'
                spacing={2}
                sx={{
                  marginTop: theme.spacing(1),
                  marginBottom: theme.spacing(1),
                  padding: theme.spacing(2),
                  backgroundColor: theme.palette.grey[100],
                }}
              >
                {!!portOfDischarge && (
                  <Tile nowrap label={t('pod', 'POD')} value={portOfDischarge} />
                )}
                {!!portOfLoading && direction === CarrierVisitDirection.Inbound && (
                  <Tile nowrap label={t('pol', 'POL')} value={portOfLoading} />
                )}
                {!!vgm && (
                  <Tile
                    nowrap
                    label={t('vgm', 'VGM')}
                    value={`${vgm.toLocaleString(undefined, { maximumFractionDigits: 0 })}kg`}
                  />
                )}
                {!vgm && !!gwt && (
                  <Tile
                    nowrap
                    label={t('gwt', 'GWT')}
                    value={`${gwt.toLocaleString(undefined, { maximumFractionDigits: 0 })}kg`}
                  />
                )}
                {!!shippingLine && (
                  <Tile nowrap label={t('shippingLine', 'Shipping Line')} value={shippingLine} />
                )}
                {!!journeyCategory && (
                  <Tile
                    nowrap
                    label={t('journeyCategory', 'Journey Category')}
                    value={journeyCategory}
                  />
                )}
                {!!containerStatus && (
                  <Tile nowrap label={t('status', 'Status')} value={containerStatus} />
                )}
              </Stack>
            </Grid>
          )}
          {containerAutocomplete ? <Grid size={12}>{containerAutocomplete}</Grid> : ''}
          {hasOperationalInstructions && (
            <Grid size={12}>
              <Controller
                control={control}
                name='operationalInstructions'
                render={({ field }) => (
                  <TextField
                    {...field}
                    data-cy='container-operational-instructions'
                    disabled
                    multiline
                    fullWidth
                    label={t('operationalInstructions', 'Operational Instructions')}
                    variant='outlined'
                  />
                )}
              />
            </Grid>
          )}

          {isOverlanded && (
            <>
              <Grid size={12}>
                <Controller
                  control={control}
                  name='containerNumber'
                  rules={{
                    required: {
                      value: true,
                      message: t('fieldIsRequired', 'Field is required.'),
                    },
                    validate: v =>
                      (!!v && validateContainerNumber(v)) ||
                      t('invalidContainerNumber', 'Invalid container number'),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      data-cy='container-number'
                      fullWidth
                      label={`${t('containerNumber', 'Container Number')}`}
                      variant='outlined'
                      type='text'
                      required
                      onChange={e => {
                        e.target.value = e.target.value.toUpperCase()
                        field.onChange(e)
                        onContainerNumberChange?.(e.target.value)
                      }}
                      error={!!errors.containerNumber}
                      helperText={errors.containerNumber?.message || ''}
                    />
                  )}
                />

                <InspectContainerAlert alertType={inspectContainerAlertType} />
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel id='status-label'>{t('status', 'Status')}</InputLabel>
                  <Controller
                    control={control}
                    name='isEmpty'
                    render={({ field }) => (
                      <Select
                        {...field}
                        fullWidth
                        variant='outlined'
                        labelId='status-label'
                        label={t('status', 'Status')}
                        onChange={e => field.onChange(e.target.value === 'true')}
                        value={field.value === true ? 'true' : 'false'}
                      >
                        <MenuItem value='true'>{t('empty', 'Empty')}</MenuItem>
                        <MenuItem value='false'>{t('full', 'Full')}</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
            </>
          )}

          {(unitTypeIsContainer || isOverlanded) && (
            <Grid size={12}>
              {isoCodeMappingInputStore.renderIsoCodeMappingInput?.({
                label: `${t('isoCode', 'ISO Code')}`,
                fullWidth: true,
                variant: 'outlined',
                type: 'text',
                uppercase: true,
                formState: formState,
                name: 'containerIsoCode',
                required: true,
                control: control,
                watch: watch,
              })}
            </Grid>
          )}

          <Grid size={12}>
            <Controller
              control={control}
              name='containerMaxGross'
              rules={{
                required: {
                  value: true,
                  message: t(
                    'pleaseEnterANumberGreaterThan0',
                    'Please enter a number greater than 0.',
                  ),
                },
                max: {
                  value: 99999,
                  message: t(
                    'pleaseEnterANumberLessThan99999',
                    'Please enter a number less than 99999.',
                  ),
                },
              }}
              render={({ field: { onChange, value, ...rest } }) => (
                <TextField
                  {...rest}
                  value={value ?? ''}
                  data-cy='container-max-gross-weight'
                  fullWidth
                  required
                  label={t('maxGrossWeight', 'Max. gross weight')}
                  variant='outlined'
                  type='number'
                  onKeyDown={notAllowPlusMinusPointSign}
                  onChange={e => {
                    onlyAllowPositiveInt(e)
                    onChange(e)
                  }}
                  InputProps={{
                    endAdornment: (
                      <CustomInputAdornment position='end' color='secondary'>
                        kg
                      </CustomInputAdornment>
                    ),
                  }}
                  error={!!errors.containerMaxGross}
                  helperText={errors.containerMaxGross?.message}
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Controller
              control={control}
              name='containerTare'
              rules={{
                required: {
                  value: true,
                  message: t(
                    'pleaseEnterANumberGreaterThan0',
                    'Please enter a number greater than 0.',
                  ),
                },
                max: {
                  value: 99999,
                  message: t(
                    'pleaseEnterANumberLessThan99999',
                    'Please enter a number less than 99999.',
                  ),
                },
              }}
              render={({ field: { onChange, value, ...rest } }) => (
                <TextField
                  {...rest}
                  value={value ?? ''}
                  data-cy='container-tare-weight'
                  required
                  fullWidth
                  label={t('tareWeight', 'Tare weight')}
                  variant='outlined'
                  type='number'
                  onKeyDown={notAllowPlusMinusPointSign}
                  onChange={e => {
                    onlyAllowPositiveInt(e)
                    onChange(e)
                  }}
                  InputProps={{
                    endAdornment: (
                      <CustomInputAdornment position='end' color='secondary'>
                        kg
                      </CustomInputAdornment>
                    ),
                  }}
                  error={!!errors.containerTare}
                  helperText={errors.containerTare?.message}
                />
              )}
            />
          </Grid>
          {hasDoorDirection && (
            <Grid size={12}>
              <Controller
                control={control}
                name='doorDirection'
                render={({ field }) => (
                  <TextField
                    {...field}
                    data-cy='container-door-direction'
                    disabled
                    fullWidth
                    label={t('doorDirection', 'Door direction')}
                    variant='outlined'
                  />
                )}
              />
            </Grid>
          )}
          {containerItem && (
            <Grid size={12}>
              <ControlledDamageRecordBox control={control} name='hasDamage' />
            </Grid>
          )}

          <Grid size={12}>
            <Controller
              control={control}
              name={'unitLabel'}
              render={({ field: { onChange, value } }) => (
                <UnitLabelSubForm
                  formStore={formStore}
                  name='unitLabel'
                  initialValue={value}
                  onChange={value => onChange(value)}
                />
              )}
            />
          </Grid>

          {!watch('isEmpty') && (
            <Grid size={12}>
              <Controller
                control={control}
                name={'oog'}
                render={({ field: { onChange, value } }) => (
                  <OogSubForm
                    formStore={formStore}
                    name='oog'
                    initialValue={value}
                    onChange={oog => onChange(oog)}
                  />
                )}
              />
            </Grid>
          )}

          {!watch('isEmpty') && (
            <Grid size={12}>
              <ControlledContainerSealsWithTypes
                hasSeals={getValues('hasSeals') ?? false}
                seals={getValues('seals') ?? []}
                onChangeHasSeals={updatedHasSeals => {
                  setValue(`hasSeals`, updatedHasSeals)
                }}
                onChangeSeals={updatedSeals => {
                  setValue(`seals`, updatedSeals)
                }}
              />
            </Grid>
          )}

          <Grid size={12}>
            <Controller
              control={control}
              name='notes'
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  fullWidth
                  label={t('notes', 'Notes')}
                  variant='outlined'
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    )
  },
)
